import { Button, Select } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import classnames from "classnames";
import { DateTime } from "luxon";
import { useState } from "react";
import { useSiteWizardMutations } from "../../../../../hooks/useSiteWizardMutations";
import { TodoStatus } from "../../../../../utils/backend-types";
import { THEME_VARS } from "../../../../../utils/constants";
import { OrangeCircle } from "../../../../OrangeCircle/OrangeCircle";
import { useShouldShowStaffView } from "../../../../StaffViewToggle/useShouldShowStaffView";
import { OnboardingTodo } from "../../../../Todos/Todos.constants";
import { getOnboardingTodoLabel } from "../../../../Todos/utils/getOnboardingTodoLabel";
import { getTodoStatusOptions } from "../../../../Todos/utils/getTodoStatusOptions";
import "./OptiNodeAssignment.scss";

type OptiNodeAssignmentProps = {
  siteId: number;
  done: boolean;
  regulatoryTodoDone: boolean;
  msbDataTodoDone: boolean;
  mbkTodoDone: boolean;
  onSiteWizardUpdated: () => Promise<void>;
};

function OptiNodeAssignment({
  siteId,
  done,
  onSiteWizardUpdated,
  regulatoryTodoDone,
  msbDataTodoDone,
  mbkTodoDone
}: OptiNodeAssignmentProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const {
    updateOnboardingProcessDoneMutation,
    updateOnboardingPhaseDoneMutation
  } = useSiteWizardMutations(siteId);

  const updateOnboardingProcessDone =
    updateOnboardingProcessDoneMutation.mutateAsync;
  const updateOnboardingPhaseDone =
    updateOnboardingPhaseDoneMutation.mutateAsync;

  function setOnboardingPhaseDoneStatus(
    todoType: OnboardingTodo,
    isDone: boolean
  ) {
    setIsSpinning(true);
    const onboardingPhase = getOnboardingTodoLabel(
      todoType,
      DateTime.now().year
    );
    updateOnboardingPhaseDone({ onboardingPhase, isDone })
      .then(onSiteWizardUpdated)
      .finally(() => {
        setIsSpinning(false);
      });
  }

  function setOnboardingProcessStatus() {
    setIsSpinning(true);
    updateOnboardingProcessDone(!done)
      .then(onSiteWizardUpdated)
      .finally(() => {
        setIsSpinning(false);
      });
  }

  function getTodoStatusElement(
    todoDone: boolean,
    type: OnboardingTodo,
    staff: boolean = false
  ) {
    if (staff) {
      return (
        <div className="todo-status">
          <TodoStatusUpdate
            staff={staff}
            todoDone={todoDone ? TodoStatus.Done : TodoStatus.Open}
            type={type}
            onUpdateOnboardingPhaseDoneStatus={setOnboardingPhaseDoneStatus}
          />
        </div>
      );
    } else if (todoDone) {
      return (
        <div className="todo-status">
          <IconCheck color={THEME_VARS.success[6]} size="1.3rem" />
        </div>
      );
    } else {
      return (
        <div className="todo-status">
          <OrangeCircle />
        </div>
      );
    }
  }

  return (
    <div className="OptiNodeAssignment">
      <div className="optinode-assignment-todo-list-container">
        <div className="todo-status-container">
          <span
            className={classnames("todo-status-title", {
              staff: shouldShowStaffView
            })}
          >
            MBK erstellen
          </span>
          {getTodoStatusElement(
            mbkTodoDone,
            OnboardingTodo.MESS_UND_BILANZIERUNGSKONZEPT_ERSTELLEN,
            shouldShowStaffView
          )}
        </div>
        <div className="todo-status-container">
          <span
            className={classnames("todo-status-title", {
              staff: shouldShowStaffView
            })}
          >
            Aufgabenliste anlegen
          </span>
          {getTodoStatusElement(
            regulatoryTodoDone,
            OnboardingTodo.AUFGABENLISTE_MIT_REGULATORISCHEN_PFLICHTEN_ANLEGEN,
            shouldShowStaffView
          )}
        </div>
        <div className="todo-status-container">
          <span
            className={classnames("todo-status-title", {
              staff: shouldShowStaffView
            })}
          >
            Datenempfang einrichten
          </span>
          {getTodoStatusElement(
            msbDataTodoDone,
            OnboardingTodo.KONTINUIERLICHER_MSB_DATENEMPFANG_EINRICHTEN,
            shouldShowStaffView
          )}
        </div>
      </div>
      {shouldShowStaffView && (
        <div className="missing-data-container">
          <Button
            className="missing-data-button"
            color="primary"
            loading={isSpinning}
            variant="default"
            onClick={setOnboardingProcessStatus}
          >
            Onboarding {done ? "zurücksetzen" : "abschließen"}
          </Button>
        </div>
      )}
    </div>
  );
}

type TodoStatusUpdateProps = {
  todoDone: TodoStatus;
  type: OnboardingTodo;
  staff: boolean;
  onUpdateOnboardingPhaseDoneStatus: (
    todoType: OnboardingTodo,
    isDone: boolean
  ) => void;
};

function TodoStatusUpdate({
  todoDone,
  type,
  staff,
  onUpdateOnboardingPhaseDoneStatus
}: TodoStatusUpdateProps) {
  return (
    <Select
      data={getTodoStatusOptions(todoDone, "", staff)}
      defaultValue={todoDone ?? false}
      id={"todo-status-update" + type}
      name="todo-status-update"
      required
      w={100}
      onChange={(newValue: string | null) => {
        if (newValue !== null) {
          onUpdateOnboardingPhaseDoneStatus(type, newValue === TodoStatus.Done);
        }
      }}
    />
  );
}

export { OptiNodeAssignment };
